import React, { useCallback, useEffect, useRef, useState, useMemo } from "react"
import CMSRender from "aldoo-ra/CMS/cms-render"
import { navigate } from "vike/client/router"
import { usePageContext } from "vike-react/usePageContext"
import MenuGroup from "aldoo-ra/MenuGroup"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Icon from "aldoo-ra/Icon"
import useSigninController from "aldoo-ra/Signin/signin-controller"
import ArrowMenuMobile from "./ArrowMenuMobile"

//tabs (fallback for routes not handled by Router)
import Subscriptions from "./Subscriptions"
import Profile from "./Profile"
import Activate from "./Activate"
import UserPaymentMethods from "./UserPaymentMethods"
import InvoiceBrowser from "./Invoices"
import Devices from "./Devices"
import AddOns from "./Add-ons"
import Purchases from "./Purchases"

//map section to tab (fallback)
const tabs = {
  subscriptions: Subscriptions,
  profile: Profile,
  licenses: Activate, // Changed from activate to licenses
  "payment-methods": UserPaymentMethods,
  invoices: InvoiceBrowser,
  devices: Devices,
  "add-ons": AddOns,
  purchases: Purchases,
}

const AccountPage = ({ section, activationMode = false }) => {
  const actionMapRef = useRef({})
  const { logout, userProfile } = useSigninController()
  const pageContext = usePageContext()

  // Use section prop or extract from URL path as fallback
  const getCurrentSection = () => {
    if (section) return section

    // Use actual browser URL instead of pageContext (which may be stale)
    const urlPath = window.location.pathname

    if (urlPath.startsWith("/account/")) {
      const pathSection = urlPath.replace("/account/", "").replace("/account", "")

      // Handle nested license routes - map both /licenses and /licenses/activate to "licenses"
      if (pathSection.startsWith("licenses")) {
        return "licenses"
      }

      return pathSection || "profile"
    }
    return "profile"
  }

  const [currentSection, setCurrentSection] = useState(getCurrentSection())
  const menuItemsRef = useRef([])
  const menuNameRef = useRef(null)
  const currentSectionRef = useRef(currentSection)

  // Update current section when URL changes or section prop changes
  useEffect(() => {
    const newSection = getCurrentSection()
    if (newSection !== currentSection) {
      setCurrentSection(newSection)
      currentSectionRef.current = newSection
    }
  }, [pageContext.urlPathname, section])

  // Navigation using proper routing system
  const navigateToSection = useCallback((sectionKey, skipURLUpdate = false) => {
    // Update local state immediately
    setCurrentSection(sectionKey)
    currentSectionRef.current = sectionKey

    // Optionally update URL without triggering remount
    if (!skipURLUpdate) {
      const newUrl = `/account/${sectionKey}`
      window.history.pushState({}, '', newUrl)
    }
  }, [])

  // Store logout and userProfile in refs to avoid re-render loop
  const logoutRef = useRef(logout)
  const userProfileRef = useRef(userProfile)
  logoutRef.current = logout
  userProfileRef.current = userProfile

  // Track selected index for mobile menu
  const [mobileMenuSelectedIndex, setMobileMenuSelectedIndex] = useState(0)

  const handleData = useCallback(
    (data) => {
      const menuData = data.data[0]?.content?.menu[0]
      const menu = menuData?.content?.items
      if (menu) {
        // Always update menu items (in case component remounted)
        const shouldInitialize = !menuItemsRef.current.length
        menuItemsRef.current = menu
        menuNameRef.current = menuData.label || menuData.name || "AccountMenu"

        if (shouldInitialize) {
          // Initialize action map for menu items once
          menu.forEach((item) => {
            const key = item.content?.options?.action
            if (key) {
              if (key === "logout") {
                // Logout should trigger dialog, not navigate
                actionMapRef.current[key] = () => logoutRef.current()
              } else if (key === "activate") {
                actionMapRef.current[key] = () => navigateToSection("licenses")
              } else {
                actionMapRef.current[key] = () => navigateToSection(key)
              }
            }
          })
        }

        // Always update the mobile menu index when menu data arrives
        const section = currentSectionRef.current
        const foundIndex = menu.findIndex(
          item => item.content?.options?.action === section
        )
        const newIndex = foundIndex >= 0 ? foundIndex : 0
        if (newIndex !== mobileMenuSelectedIndex) {
          setMobileMenuSelectedIndex(newIndex)
        }
      }
    },
    [navigateToSection, mobileMenuSelectedIndex]
  )

  // Determine which tab component to display and activation mode
  const { TabComponent, shouldShowActivationMode } = useMemo(() => {
    const currentPath = pageContext.urlPathname

    // Handle /account/licenses/activate - should show licenses section with activation mode
    if (currentPath === '/account/licenses/activate') {
      return {
        TabComponent: tabs.licenses,
        shouldShowActivationMode: true
      }
    }

    // Handle all account routes internally using section-based tabs
    // This ensures the side menu layout is always preserved
    return {
      TabComponent: tabs[currentSection] || tabs.profile,
      shouldShowActivationMode: currentPath === '/account/licenses/activate'
    }
  }, [pageContext.urlPathname, currentSection])

  // Update mobile menu selected index when currentSection changes
  useEffect(() => {
    currentSectionRef.current = currentSection

    // Only update if menu items are loaded
    if (!menuItemsRef.current || menuItemsRef.current.length === 0) {
      return
    }

    const foundIndex = menuItemsRef.current.findIndex(
      item => item.content?.options?.action === currentSection
    )
    const newIndex = foundIndex >= 0 ? foundIndex : 0

    if (newIndex !== mobileMenuSelectedIndex) {
      setMobileMenuSelectedIndex(newIndex)
    }
  }, [currentSection, mobileMenuSelectedIndex])

  // Memoize the tab content to prevent re-renders when only section changes
  const memoizedTabContent = useMemo(() => {
    return <TabComponent activationMode={shouldShowActivationMode || activationMode} />
  }, [TabComponent, currentSection, shouldShowActivationMode, activationMode])

  // Memoize renderItem to prevent CMSRender from re-rendering
  const renderItem = useCallback((page) => {
    const username = userProfileRef.current?.name || "User"
    const title = page.title.replace("{{username}}", username)

    return (
      <div className="relative min-h-screen w-full px-4 md:px-[56px] lg:px-[56px] xl:px-[76px]">
        <div className="min-h-screen w-full xl:max-w-[1536px] mx-auto">
          <div
            className="w-full h-[88px] lg:h-[108px] xl:h-[128px] flex items-end justify-center md:justify-start border-b border-bordered py-[30px] bg-paper"
            style={{ zIndex: 100 }}
          >
            <Typography
              component="h1"
              variant="h3"
              className="text-center md:text-left"
            >
              {title}
            </Typography>
          </div>

          <div
            className="w-full flex flex-col lg:flex-row lg:gap-5 lg:pt-16"
            style={{ zIndex: 50 }}
          >
            {/* Desktop Menu */}
            <div className="hidden lg:block w-[240px]">
              <MenuGroup
                name={page.menu[0].name}
                renderAs="vlist"
                skipRootItem
                actionMap={actionMapRef.current}
                styleOverrides={{
                  menu_item: "px-0 my-0",
                }}
                menuItemRender={(item) => (
                  <Button
                    variant="nav_menuitem"
                    className={`w-full h-[44px] flex text-xl hover:text-primary px-0 my-4 transition-colors ${
                        currentSection === item.options?.action ||
                        (currentSection === "licenses" && item.options?.action === "activate")
                          ? "text-primary font-bold"
                          : "text-gray_2 font-normal"
                      }`}
                    onClick={() => {
                      const action = item.options?.action
                      if (action && actionMapRef.current[action]) {
                        actionMapRef.current[action]()
                      }
                    }}
                  >
                    {item.label}
                  </Button>
                )}
              />
            </div>

            <div className="w-full flex-1 mt-8 lg:mt-0">
              {memoizedTabContent}
            </div>
          </div>
        </div>
      </div>
    )
  }, [currentSection, memoizedTabContent])

  return (
    <>
      {/* Mobile Menu - rendered outside CMSRender to prevent remounting */}
      <div className="pt-[80px] lg:pt-0">
        {menuNameRef.current && (
          <div className="lg:hidden">
            <ArrowMenuMobile
              key={`mobile-menu-${menuNameRef.current}`}
              menuGroupName={menuNameRef.current}
              initialSelectedIndex={mobileMenuSelectedIndex}
              enableScrollNavigation={false}
              onSelectionChange={(index, item) => {
                const action = item.content?.options?.action
                if (action && actionMapRef.current[action]) {
                  actionMapRef.current[action]()
                }
              }}
              onData={(payload) => {
                // Menu data received - update the selected index if needed
                const section = getCurrentSection()
                const items = payload?.data[0]?.content?.items || []
                const updatedIndex = items.findIndex(
                  item => item.content?.options?.action === section
                )
                if (updatedIndex >= 0 && updatedIndex !== mobileMenuSelectedIndex) {
                  setMobileMenuSelectedIndex(updatedIndex)
                }
              }}
              menuItemRender={(item, _, isSelected) => (
                <div
                  className={`min-w-[150px] h-[40px] flex items-center justify-center px-0 transition-all duration-300
                    ${
                      isSelected
                        ? "text-primary font-bold scale-105"
                        : "text-gray_2 scale-100"
                    }`}
                >
                  <span className="text-base text-center">
                    {item.label}
                  </span>
                </div>
              )}
            />
          </div>
        )}
      </div>

      <CMSRender
        key="account-page-layout"
        contentType="ProfilePage"
        onData={handleData}
        renderItem={renderItem}
    />
    </>
  )
}

export default AccountPage
